import { useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { PaperPlaneTilt, ArrowBendUpLeft, Paperclip, WarningCircle } from "@phosphor-icons/react";
import { fmtDT } from "../hs/hsUtils";
import { openAuthFile } from "../../lib/fileOpen";
import ReplyAttachmentPicker from "./ReplyAttachmentPicker";
import QuickReplyPicker from "./QuickReplyPicker";

/** Reply to the sender straight from the inbox. Sent through the company's e-mail sender with the JR reference in the
 *  subject (so their answer threads back), optionally with files / quotes / RAMS / form PDFs attached, and logged as a
 *  note + timeline entry on the linked request. */
export default function InboxReplyBox({ message, onSent, smtpReady = true, onOpenSettings }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(`Re: ${message.subject || "your email"}`);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const replies = message.replies || [];
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/inbox/messages/${message.id}/reply`, { text, subject, attachments: files.map((f) => f.id) });
      toast.success(data.message);
      setText(""); setFiles([]); setOpen(false); onSent && onSent(data.reply);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't send"); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-border" data-testid="inbox-reply">
      {replies.length > 0 && (
        <div className="px-4 pt-3 space-y-2" data-testid="inbox-reply-list">
          {replies.map((r) => (
            <div key={r.id} className="rounded-md border border-border bg-muted/20 p-2 text-xs" data-testid={`inbox-reply-${r.id}`}>
              <p className="text-muted-foreground"><ArrowBendUpLeft size={12} className="inline mr-1" /><strong>{r.by}</strong> replied · {fmtDT(r.at)} · <span className="font-medium">{r.subject}</span></p>
              <p className="mt-1 whitespace-pre-wrap">{r.text}</p>
              {r.attachments?.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`inbox-reply-${r.id}-attachments`}>
                  {r.attachments.map((a, i) => a.url
                    ? <button key={i} type="button" onClick={() => openAuthFile(a.url)} className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] hover:border-primary/50"><Paperclip size={11} /> {a.filename}</button>
                    : <span key={i} className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px]"><Paperclip size={11} /> {a.filename}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="p-4 space-y-2">
        {!smtpReady ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-200" data-testid="inbox-reply-smtp-missing">
            <WarningCircle size={14} weight="fill" className="shrink-0" />
            <span>Replying from the inbox needs the <strong>Sender replies (SMTP)</strong> mailbox set up first.</span>
            {onOpenSettings && <Button size="sm" variant="outline" className="h-6 text-[11px] ml-auto" onClick={onOpenSettings} data-testid="inbox-reply-open-settings">Open mailbox settings</Button>}
          </div>
        ) : !open ? (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={!message.from_email} data-testid="inbox-reply-open"><ArrowBendUpLeft size={14} className="mr-1" /> Reply to {message.from_name || message.from_email}</Button>
        ) : (
          <>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-8 text-xs" data-testid="inbox-reply-subject" />
            <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={`Hi ${message.from_name?.split(" ")[0] || "there"},`} className="text-sm" autoFocus data-testid="inbox-reply-text" />
            <div className="flex flex-wrap items-start gap-2">
              <QuickReplyPicker messageId={message.id} onPick={(s) => { setText((t) => (t.trim() ? `${t.trimEnd()}\n\n${s.text}` : s.text)); if (s.subject) setSubject(s.subject); }} />
              <ReplyAttachmentPicker messageId={message.id} value={files} onChange={setFiles} />
            </div>
            <p className="text-[11px] text-muted-foreground">Goes to {message.from_email}{message.request_reference ? ` · subject carries ${message.request_reference} so their answer lands on the request` : ""} · the original email is quoted underneath.</p>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setFiles([]); }} disabled={busy}>Cancel</Button>
              <Button size="sm" onClick={send} disabled={busy || !text.trim()} data-testid="inbox-reply-send"><PaperPlaneTilt size={14} className="mr-1" /> {busy ? "Sending…" : files.length ? `Send with ${files.length} file${files.length > 1 ? "s" : ""}` : "Send reply"}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
